import { DatePicker as WebSDKDatePicker } from '@zambezi/sdk/date-picker';
// eslint-disable-next-line import/no-unresolved
import { AllowedFormat } from '@zambezi/sdk/date-picker/interfaces';
import dayjs from 'dayjs';
import React, { forwardRef, useEffect, useId } from 'react';
import FormControl from '../FormControl';

export interface DatePickerProps {
  label?: string;
  name: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | boolean;
  value?: string;
  tooltip?: string | React.ReactNode | React.ReactNode[];
  tooltipAriaLabel?: string;
  initialViewDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  dateFormat?: AllowedFormat;
  openOnIconFocus?: boolean;
  helpText?: React.ReactNode | React.ReactNode[];
  pii?: boolean;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disableDayFn?: (date: Date) => boolean;
  onUserChange?: (date: Date) => void;
}

const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      label,
      placeholder,
      disabled = false,
      required = false,
      pii = false,
      readOnly = false,
      value,
      error,
      tooltip,
      tooltipAriaLabel,
      helpText,
      initialViewDate,
      minDate,
      maxDate,
      dateFormat = 'DD/MM/YYYY',
      openOnIconFocus = false,
      onChange,
      onBlur,
      onFocus,
      disableDayFn,
      onUserChange
    },
    ref
  ) => {
    const id = useId();

    useEffect(() => {
      // Fixes a visual bug where dynamic date picker error is not reflected correctly in UI unless the input is focused in and focused out
      if (error) {
        document.getElementById(id).parentElement.classList.add('zb-date-picker-is-error');
      } else {
        document.getElementById(id).parentElement.classList.remove('zb-date-picker-is-error');
      }
    }, [error]);

    const onDateChange = (date: Date) => {
      onChange(dayjs(date).format('YYYY-MM-DD'));
    };

    return (
      <FormControl
        id={id}
        label={label}
        tooltip={tooltip}
        tooltipAriaLabel={tooltipAriaLabel}
        error={error}
        helpText={helpText}
        required={required}
        pii={pii}
      >
        <WebSDKDatePicker
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          error={!!error}
          initialViewDate={initialViewDate}
          minDate={minDate}
          maxDate={maxDate}
          date={value ? new Date(value) : undefined}
          dateFormat={dateFormat}
          onChange={onDateChange}
          onInputChange={(date) => onChange(dayjs(date, dateFormat).format('YYYY-MM-DD'))}
          onBlur={onBlur}
          onFocus={onFocus}
          ref={ref}
          // @ts-expect-error ariaLabel is missing in WebSDK interface
          ariaLabel={label}
          aria-describedby={error ? `error-${id}` : undefined}
          aria-required={required}
          openOnInputFocus
          openOnIconFocus={openOnIconFocus}
          readOnly={readOnly}
          disableDayFn={disableDayFn}
          onUserChange={onUserChange}
        />
      </FormControl>
    );
  }
);

export default DatePicker;
